<script setup lang="ts">
/**
 * Version Alert Component
 *
 * Displays a notification about the new portal version with options to:
 * - Switch back to the old version
 * - Hide the alert temporarily
 * - Permanently disable the alert
 */

import { computed, onMounted } from 'vue';

import { useUIVersionAlert } from '@/composables/useUIVersionAlert';

const oldVersionUrl = computed(() => import.meta.env.VITE_OLD_VERSION_URL);

const {
  isVisible,
  isHiding,
  checkShouldShowAlert,
  hideAlert,
  hideAlertPermanently,
} = useUIVersionAlert();

onMounted(() => {
  checkShouldShowAlert();
});
</script>

<template>
  <Transition name="fade">
    <div v-if="isVisible" class="version-alert" role="alert">
      <div class="version-alert__content">
        <div class="version-alert__message">
          <span>You are currently using the new version of the portal. </span>
          <a :href="oldVersionUrl" class="version-alert__link">
            Go to the old version
            <v-icon icon="mdi-open-in-app" class="ml-1" />
          </a>
        </div>

        <div class="version-alert__actions">
          <button
            class="version-alert__action"
            aria-label="Don't show this message again"
            @click.prevent="hideAlertPermanently"
          >
            Don't show this again
            <v-progress-circular
              v-if="isHiding"
              size="16"
              width="2"
              color="grey"
              indeterminate
              class="ml-1"
            />
            <v-icon v-else icon="mdi-eye-off" class="ml-1" />
          </button>
          <v-btn
            class="version-alert__close"
            icon="mdi-close"
            variant="text"
            density="comfortable"
            size="small"
            aria-label="Close alert"
            @click="hideAlert"
          />
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.version-alert {
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
  min-width: 600px;
  max-width: 72%;
  background: colors.$color-orange-50;
  border: 1px solid colors.$color-orange-200;
  border-radius: 0 0 8px 8px;
  padding: 0;
  font-size: 14px;
  color: colors.$color-text-secondary;
  height: 48px;
  display: flex;
  align-items: center;
  z-index: 10000;
  box-shadow: 0 2px 8px colors.$color-shadow-black-10;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.version-alert__content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 16px;
  gap: 16px;
  height: 100%;
}

.version-alert__message {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  white-space: nowrap;
}

.version-alert__link {
  color: colors.$color-orange-400;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;

  &:hover {
    color: colors.$color-orange-300;
  }

  :deep(i) {
    text-decoration: none !important;
  }
}

.version-alert__actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.version-alert__action {
  color: colors.$color-text-tertiary;
  text-decoration: none;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: none;

  &:hover {
    color: colors.$color-text-primary;
  }

  :deep(i) {
    text-decoration: none !important;
  }
}

.version-alert__close {
  background: none;
  border: none;
  width: 32px;
  height: 32px;
  cursor: pointer;
  color: colors.$color-gray-400;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    color: colors.$color-gray-600;
    background: colors.$color-gray-100;
  }

  i {
    font-size: 16px;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -100%);
}
</style>
